import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Clock, TrendingUp, BarChart3, Calendar, RefreshCw, DollarSign } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const TotalAssetLifecycle = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lifecyclePhases = [
    {
      phase: 'Installation & Commissioning',
      description: 'Proper initial protection and baseline documentation for new equipment.',
      services: ['Pre-service coatings', 'Baseline inspections', 'Documentation setup', 'Protection planning']
    },
    {
      phase: 'Operational Monitoring',
      description: 'Regular assessments to track equipment condition and plan interventions.',
      services: ['Scheduled inspections', 'Condition monitoring', 'Wear tracking', 'Performance analysis']
    },
    {
      phase: 'Preventive Maintenance',
      description: 'Proactive repairs and protection to prevent failures before they occur.',
      services: ['Planned repairs', 'Coating maintenance', 'Component rebuilding', 'System upgrades']
    },
    {
      phase: 'Life Extension',
      description: 'Strategic interventions to extend equipment beyond original design life.',
      services: ['Major overhauls', 'Structural reinforcement', 'System modernization', 'Performance restoration']
    }
  ];

  const economicBenefits = [
    {
      icon: DollarSign,
      title: 'Reduced Capital Expenditure',
      description: 'Extend equipment life to defer costly replacements by 5-10 years.'
    },
    {
      icon: TrendingUp,
      title: 'Improved ROI',
      description: 'Maximize return on your original equipment investment.'
    },
    {
      icon: BarChart3,
      title: 'Predictable Budgeting',
      description: 'Plan maintenance costs years in advance with confidence.'
    },
    {
      icon: Clock,
      title: 'Reduced Downtime',
      description: 'Scheduled maintenance vs. emergency repairs means less lost production.'
    }
  ];

  const programFeatures = [
    'Comprehensive asset inventory',
    'Condition assessment database',
    'Predictive maintenance scheduling',
    'Budget planning tools',
    'Priority ranking system',
    'Historical performance tracking',
    'Compliance documentation',
    'Regular program reviews'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Total Asset Lifecycle Management" description="Total asset lifecycle management. Extend equipment service life with proactive coating and repair programs." path="/maintenance/total-asset-lifecycle" />
      
      <PageHero
        badge="SMART Program"
        title="Total Asset Lifecycle"
        description="Comprehensive management from installation through end-of-life optimization. We partner with you to maximize the value of every asset in your facility."
        backgroundKey="total-asset-lifecycle"
        buttonText="Start Lifecycle Program"
      />

      {/* Lifecycle Phases */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Asset Lifecycle Phases</h2>
          <div className="relative">
            {/* Timeline connector */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-green-200 -translate-y-1/2"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {lifecyclePhases.map((phase, index) => (
                <Card key={index} className="relative z-10 border-t-4 border-t-green-700">
                  <CardHeader>
                    <div className="w-10 h-10 bg-green-700 text-white rounded-full flex items-center justify-center font-bold mb-3">
                      {index + 1}
                    </div>
                    <CardTitle className="text-lg">{phase.phase}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4">{phase.description}</p>
                    <ul className="space-y-1">
                      {phase.services.map((service, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-center">
                          <CheckCircle className="text-green-700 mr-2 flex-shrink-0" size={14} />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Economic Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Economic Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {economicBenefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <IconComponent className="mx-auto text-green-700 mb-4" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Lifecycle Program Features</h2>
              <p className="text-gray-600 mb-8">
                Our Total Asset Lifecycle program provides comprehensive tools and services to manage 
                your equipment from day one through retirement. We become your partner in maximizing 
                equipment value and minimizing total cost of ownership.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {programFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="text-green-700 mr-2 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-green-700 text-white p-8 rounded-lg">
              <RefreshCw className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Continuous Improvement</h3>
              <p className="text-green-100 mb-6">
                Our lifecycle program isn't static. We continuously analyze performance data, 
                incorporate new technologies, and refine our approach to deliver ever-improving results.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="mr-2" size={18} />
                  Annual program reviews
                </li>
                <li className="flex items-center">
                  <CheckCircle className="mr-2" size={18} />
                  Technology updates
                </li>
                <li className="flex items-center">
                  <CheckCircle className="mr-2" size={18} />
                  Best practice sharing
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Calendar className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Asset Lifecycle?</h2>
          <p className="text-blue-100 mb-8">
            Let us develop a customized lifecycle management program for your facility.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Request Program Consultation <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TotalAssetLifecycle;
